// The logo: a letter burning from one corner. The Ember flame (FlameMark's
// shape) and two smaller tongues rise from the ragged edge where the page has
// already gone. The fire is masked off the page, so its base is that edge
// itself rather than a flame laid on top. Masks rather than shapes painted in
// the background colour, so the mark sits on any surface. Keep
// public/favicon.svg in step with it. FlameMark stays the icon for the burn
// action on buttons, where this is too detailed.

import { useId } from 'react';

const FLAME = 'M16 3c1.5 5 7 7.5 7 14a7 7 0 0 1-14 0c0-3.2 1.6-5.6 3.4-7.2.4 2.4 1.6 3.9 3.1 4.4C14.8 10.5 15 6.5 16 3z';
// FlameMark is drawn base-down at (16, 24); this sets flames along the burnt edge.
const flameAt = (x: number, y: number, scale: number) => `translate(${x} ${y}) scale(${scale}) translate(-16 -24)`;
// The burnt edge, from the top of the page diagonally down to its right side,
// in steps; the fire along it stops two steps short, where the big flame's
// curve takes over.
const EDGE_FIRE =
  'c.5 1 1.3 1.5 2.3 1.3.1 1.1.8 1.8 1.9 1.8 0 1.2.7 2 1.9 2 0 1.2.7 2 1.9 2 0 1.2.7 2 1.9 2';
const EDGE = `${EDGE_FIRE} 0 1.2.7 2 1.9 2 .2.8.5 1.4.7 1.9`;
const SHEET = `M6 10a2 2 0 0 1 2-2h3.5${EDGE}V28a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z`;

// 36 by default: the page and flame share the box, so it needs a little more
// room than FlameMark to hold its own beside the wordmark.
export default function LogoMark({ size = 36 }: { size?: number }) {
  // useId's punctuation isn't safe inside url(#…).
  const id = useId().replace(/[^\w-]/g, '');
  const onPage = `${id}-on`;
  const offPage = `${id}-off`;
  const linesCut = `${id}-lines`;
  const accent = 'var(--color-accent)';
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id={onPage}>
          <path d={SHEET} />
        </clipPath>
        <mask id={offPage} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
          <rect width="32" height="32" fill="#fff" />
          <path d={SHEET} fill="#000" />
        </mask>
        <mask id={linesCut} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
          <rect width="32" height="32" fill="#fff" />
          <path d="M9 13h3M9 18h6.5M9 23h10" stroke="#000" strokeWidth="1.8" strokeLinecap="round" />
        </mask>
      </defs>
      <path d={SHEET} fill="var(--color-text)" mask={`url(#${linesCut})`} />
      {/* The glow just inside the edge, and the fire just outside it. */}
      <path
        d={`M11.5 8${EDGE}`}
        fill="none"
        stroke={accent}
        strokeWidth="3"
        strokeLinejoin="round"
        clipPath={`url(#${onPage})`}
      />
      <g fill={accent} mask={`url(#${offPage})`}>
        <path d={`M11.5 8${EDGE_FIRE}`} fill="none" stroke={accent} strokeWidth="3.4" strokeLinejoin="round" />
        <path d={FLAME} transform={flameAt(12.6, 10.4, 0.24)} />
        <path d={FLAME} transform={flameAt(15.2, 13.4, 0.34)} />
        <path d={FLAME} transform={flameAt(18.3, 19, 0.74)} />
      </g>
      <circle cx="27" cy="11.6" r=".9" fill={accent} />
      <circle cx="12.4" cy="3.2" r=".6" fill={accent} />
      <circle cx="26.4" cy="3.6" r=".6" fill={accent} />
    </svg>
  );
}
